import { cn } from '@/lib/utils';
import { EventDetailCard } from './EventDetailCard';
import type { EventContentArg } from '@fullcalendar/core';
import type { CalendarEvent } from '@/types/leave-calendar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/tooltip';

interface LeaveEventProps {
  eventInfo: EventContentArg;
}

/**
 * A React component that displays an event on the calendar.
 *
 * @param The event information passed from the FullCalendar library.
 * @returns A React component.
 */
export function LeaveEvent({ eventInfo }: LeaveEventProps) {
  const { start, startStr } = eventInfo.event;
  const { className, label, type, leave, leaveRange } = eventInfo.event
    .extendedProps as CalendarEvent['extendedProps'];

  const isHoliday = type === 'holiday';

  const Prefix = () => {
    if (!isHoliday) return null;
    return <span>🎉</span>;
  };

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <div className={cn('badge', className)}>
          <Prefix /> {label}
        </div>
      </TooltipTrigger>

      <TooltipContent
        side="top"
        className="p-0 border-none shadow-xl bg-white text-gray-900 pointer-events-none z-60"
      >
        {isHoliday ? (
          <div className="p-3 flex items-center gap-2 min-w-[180px] max-w-[280px]">
            <span>🎉</span>
            <span className="font-semibold text-gray-900 text-sm">{label}</span>
          </div>
        ) : (
          <EventDetailCard
            leave={leave}
            range={leaveRange}
            badgeClass={className}
            date={start ?? startStr ?? new Date()}
          />
        )}
      </TooltipContent>
    </Tooltip>
  );
}
